import { partition, remove, uniq } from 'es-toolkit';
import React from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, CssBaseline, Dialog, styled } from '@mui/material';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';

import { LoginContainer } from '@paragon/login-tools';
import { enqueueError, Notifier } from '@paragon/notification-tools';
import { SpinnerMessage } from '@paragon/task-spinner';
import { sortBy } from '@paragon/utilities';

import reviewLogoUrl from '../../resources/review.png';
import { startApp } from '../actions/appActions';
import { pFetchShallowPdfs } from '../data/jobData';
import { RootState } from '../store';
import { JobWithUsers } from '../store/jobsSlice';
import { setIsLoadingPdfs } from '../store/statusesSlice';
import Controls from './controls/Controls';
import Header from './header/Header';
import DownloadGrid from './popups/DownloadGrid';
import DownloadJob from './popups/DownloadJob';
import ProjectGrid from './ProjectGrid';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': { color: 'white' },
}));

export interface OnShowDownloadPopupParams {
  jobSummary: JobWithUsers;
  jobId: number;
  miscFilesButtonVisible: boolean;
  originalZipUrl: string | null;
  separatedPdfsUrl: string | null;
  mergedPdfsUrl: string | null;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

type Props = StateProps & DispatchProps;

interface State {
  popup: React.ReactNode | null;
}

class RootComponent extends React.Component<Props, State> {
  state: State = {
    popup: null,
  };

  scrollRef = React.createRef<HTMLDivElement>();

  closePopup = () => {
    this.setState({ popup: null });
  };

  onShowFileManagerPopup = (fileManagerPopup: React.ReactNode) => {
    this.setState({ popup: fileManagerPopup });
  };

  onShowDownloadPopup = (params: OnShowDownloadPopupParams) => {
    this.setState({
      popup: (
        <Dialog open>
          <DownloadJob
            jobSummary={params.jobSummary}
            jobId={params.jobId}
            miscFilesButtonVisible={params.miscFilesButtonVisible}
            originalZipUrl={params.originalZipUrl}
            separatedUrl={params.separatedPdfsUrl}
            mergedUrl={params.mergedPdfsUrl}
            onCloseClick={this.closePopup}
          />
        </Dialog>
      ),
    });
  };

  onShowDownloadGridPopup = async () => {
    // Filter to selected jobs that have been stamped
    const selectedJobs = this.props.jobs.filter((job) => {
      const selected = this.props.selectedJobIds.includes(job.id);
      if (selected) {
        return job.jobStatus === 'Complete' && (job as any).stampedBy !== null;
      }

      return false;
    });

    if (selectedJobs.length === 0) {
      this.props.enqueueError('You can only merge jobs that have a status of "Complete"');
      return;
    }

    if (uniq(selectedJobs.flatMap((job) => job.regions)).length > 1) {
      this.props.enqueueError('These jobs were sealed for different regions and cannot be merged.');
      return;
    }

    this.props.setIsLoadingPdfs(true);
    try {
      const prunedSelectedJobs = await Promise.all(
        selectedJobs.map(async (job) => {
          const jobId = job.id;
          const pdfs = await pFetchShallowPdfs(jobId);
          const foundJob = this.props.jobs.find((j) => j.id === jobId);
          const partitionedPdfs = partition(
            sortBy(pdfs, (pdf) => pdf.name),
            (pdf) => pdf.componentDesignId != null,
          );
          const componentPdfs = partitionedPdfs[0];
          const metaPdfs = partitionedPdfs[1];
          const coverPage = remove(metaPdfs, (pdf) => pdf.name === 'CoverPage.pdf');
          const backPage = remove(metaPdfs, (pdf) => pdf.name === 'BackPage.pdf');
          return {
            id: foundJob!.id,
            name: foundJob!.name,
            files: coverPage.concat(componentPdfs, metaPdfs, backPage),
          };
        }),
      );
      this.props.setIsLoadingPdfs(false);
      this.setState({
        popup: (
          <Dialog open>
            <DownloadGrid jobs={prunedSelectedJobs} onCloseClick={this.closePopup} />
          </Dialog>
        ),
      });
    } catch (err) {
      this.props.setIsLoadingPdfs(false);
    }
  };

  render() {
    const { startApp } = this.props;

    let spinnerMessage;
    if (this.props.isLoadingJobs) {
      spinnerMessage = <SpinnerMessage text="Loading Jobs" key="Loading Jobs" />;
    } else if (this.props.isUpdatingJob) {
      spinnerMessage = <SpinnerMessage text="Updating Job(s)" key="Updating Job(s)" />;
    } else if (this.props.isInsertingJob) {
      spinnerMessage = <SpinnerMessage text="Uploading Component Designs" key="Uploading Component Designs" />;
    } else if (this.props.isLoadingPdfsForMerging) {
      spinnerMessage = <SpinnerMessage text="Loading PDFs" key="Loading PDFs" />;
    } else if (this.props.multiMergePdfsGenerating > 0) {
      spinnerMessage = <SpinnerMessage text="Downloading" key="Downloading" />;
    }

    return (
      <>
        <CssBaseline />
        <SnackbarProvider
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          maxSnack={3}
          Components={{
            default: StyledMaterialDesignContent,
            success: StyledMaterialDesignContent,
            error: StyledMaterialDesignContent,
            warning: StyledMaterialDesignContent,
            info: StyledMaterialDesignContent,
          }}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              bgcolor: 'backContrast.dark',
            }}
          >
            <LoginContainer
              onLogIn={async () => {
                await startApp();
              }}
              clientLogoUrl={reviewLogoUrl}
              clientName="Review"
              requirePermission="review-access"
            >
              <Notifier />
              <Box sx={{ zIndex: 1201 }}>
                <Header />
              </Box>
              {spinnerMessage}
              <Box ref={this.scrollRef} sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
                <Controls
                  onShowDownloadPopup={this.onShowDownloadPopup}
                  onShowDownloadGridPopup={this.onShowDownloadGridPopup}
                  onShowFileManagerPopup={this.onShowFileManagerPopup}
                  closePopup={this.closePopup}
                />
                <ProjectGrid
                  scrollToTop={() => {
                    if (this.scrollRef.current) {
                      this.scrollRef.current.scrollTo(0, 0);
                    }
                  }}
                />
                {this.state.popup}
              </Box>
            </LoginContainer>
          </Box>
        </SnackbarProvider>
      </>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.logInData.loggedInUser,
  isInsertingJob: state.statuses.isInsertingJob,
  isLoadingJobs: state.statuses.isLoadingJobs,
  isLoadingPdfsForMerging: state.statuses.isLoadingPdfsForMerging,
  isUpdatingJob: state.statuses.isUpdatingJob,
  jobs: state.jobs.jobs,
  multiMergePdfsGenerating: state.statuses.multiMergePdfsGenerating,
  selectedJobIds: state.jobs.selectedJobIds,
});

const actionCreators = {
  enqueueError,
  startApp,
  setIsLoadingPdfs,
};

export default connect(mapStateToProps, actionCreators)(RootComponent);
